<template>
  <div class="contact">
    <!-- Hero Section -->
    <section class="bg-bleumarine text-white py-16">
      <div class="container mx-auto px-4">
        <h1 class="text-3xl md:text-4xl font-bold text-center mb-4">Contactez-nous</h1>
        <p class="text-center text-turquoise max-w-2xl mx-auto">
          Nous sommes à votre disposition pour répondre à toutes vos questions et vous accompagner dans vos projets.
        </p>
      </div>
    </section>

    <!-- Contact Forms -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto">
          <!-- Tabs -->
          <div class="flex mb-8 border-b">
            <button
              @click="activeTab = 'contact'"
              :class="activeTab === 'contact' ? 'border-b-2 border-bleumarine text-bleumarine' : 'text-gray-600 hover:text-bleumarine'"
              class="px-6 py-3 font-medium transition"
            >
              Formulaire de Contact
            </button>
            <button
              @click="activeTab = 'quote'"
              :class="activeTab === 'quote' ? 'border-b-2 border-violet text-violet' : 'text-gray-600 hover:text-violet'"
              class="px-6 py-3 font-medium transition"
            >
              Demande de Devis
            </button>
          </div>

          <!-- Contact Form -->
          <div v-if="activeTab === 'contact'" class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 class="text-2xl font-bold mb-6">Envoyez-nous un message</h2>
              <form @submit.prevent="submitContactForm" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nom complet *</label>
                  <input
                    type="text"
                    v-model="contactForm.name"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                    required
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    v-model="contactForm.email"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                    required
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    v-model="contactForm.phone"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Sujet *</label>
                  <SearchableSelect
                    v-model="contactForm.subject"
                    :options="subjectOptions"
                    placeholder="Sélectionnez un sujet"
                    search-placeholder="Rechercher un sujet..."
                    required
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    v-model="contactForm.message"
                    rows="5"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                    required
                  ></textarea>
                </div>
                <button type="submit" class="btn-primary w-full">
                  Envoyer le message
                </button>
              </form>
            </div>

            <!-- Contact Info -->
            <div>
              <h2 class="text-2xl font-bold mb-6">Nos Coordonnées</h2>
              <div class="space-y-6">
                <div class="flex items-start">
                  <div class="w-12 h-12 bg-bleumarine rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin class="w-6 h-6" style="color: #fff;" />
                  </div>
                  <div>
                    <h3 class="font-semibold mb-1">Adresse</h3>
                    <p class="text-gray-600">BP 561 Bingerville, Côte d'Ivoire</p>
                  </div>
                </div>
                <div class="flex items-start">
                  <div class="w-12 h-12 bg-turquoise rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail class="w-6 h-6" style="color: #fff;" />
                  </div>
                  <div>
                    <h3 class="font-semibold mb-1">Email</h3>
                    <p class="text-gray-600">infos@techspartner.com</p>
                  </div>
                </div>
                <div class="flex items-start">
                  <div class="w-12 h-12 bg-violet rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Phone class="w-6 h-6" style="color: #fff;" />
                  </div>
                  <div>
                    <h3 class="font-semibold mb-1">Téléphone</h3>
                    <p class="text-gray-600">+225 21 50 03 48 12 / +225 01 50 54 74</p>
                  </div>
                </div>
                <div class="flex items-start">
                  <div class="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Clock class="w-6 h-6" style="color: #fff;" />
                  </div>
                  <div>
                    <h3 class="font-semibold mb-1">Horaires</h3>
                    <p class="text-gray-600">Lundi - Vendredi: 8h00 - 17h00</p>
                  </div>
                </div>
              </div>

              <!-- Map placeholder -->
              <div class="mt-8 bg-gray-200 rounded-lg h-48 flex items-center justify-center">
                <span class="text-gray-400">Carte interactive</span>
              </div>
            </div>
          </div>

          <!-- Quote Form -->
          <div v-else class="max-w-3xl mx-auto">
            <h2 class="text-2xl font-bold mb-6">Demande de Devis</h2>
            <form @submit.prevent="submitQuoteForm" class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Nom complet *</label>
                  <input
                    type="text"
                    v-model="quoteForm.name"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                    required
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
                  <input
                    type="text"
                    v-model="quoteForm.company"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                  >
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    v-model="quoteForm.email"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                    required
                  >
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                  <input
                    type="tel"
                    v-model="quoteForm.phone"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                    required
                  >
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Service souhaité *</label>
                <SearchableSelect
                  v-model="quoteForm.service"
                  :options="serviceOptions"
                  placeholder="Sélectionnez un service"
                  search-placeholder="Rechercher un service..."
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Description du projet *</label>
                <textarea
                  v-model="quoteForm.description"
                  rows="5"
                  class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary-500"
                  placeholder="Décrivez votre projet et vos besoins..."
                  required
                ></textarea>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Budget estimé</label>
                <SearchableSelect
                  v-model="quoteForm.budget"
                  :options="budgetOptions"
                  placeholder="Sélectionnez une fourchette"
                  search-placeholder="Rechercher une fourchette..."
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Documents (optionnel)</label>
                <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileUpload"
                    multiple
                    accept=".pdf"
                    class="hidden"
                  >
                  <button
                    type="button"
                    @click="triggerFileUpload"
                    class="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Cliquez pour uploader des fichiers
                  </button>
                  <p class="text-sm text-gray-500 mt-2">PDF uniquement (Max 10MB)</p>
                  <div v-if="uploadedFiles.length > 0" class="mt-4 space-y-2">
                    <div v-for="(file, index) in uploadedFiles" :key="index" class="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                      <span class="text-sm text-gray-700">{{ file.name }}</span>
                      <button
                        type="button"
                        @click="removeFile(index)"
                        class="text-red-500 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <button type="submit" class="btn-primary w-full">
                Envoyer la demande de devis
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { MapPin, Mail, Phone, Clock } from 'lucide-vue-next'
import SearchableSelect from '../components/SearchableSelect.vue'

const activeTab = ref('contact')

const subjectOptions = [
  { value: 'general', label: 'Question générale' },
  { value: 'service', label: 'Demande de service' },
  { value: 'partnership', label: 'Partenariat' },
  { value: 'support', label: 'Support technique' },
  { value: 'other', label: 'Autre' }
]

const serviceOptions = [
  { value: 'datacenter', label: 'Datacenter & Productivité' },
  { value: 'network', label: 'Réseaux & Télécommunications' },
  { value: 'security', label: 'Sécurité SI' },
  { value: 'development', label: 'Développement & Bases de données' },
  { value: 'audit', label: 'Audit & Gouvernance IT' },
  { value: 'hardware', label: 'Matériels & Sécurité électronique' },
  { value: 'training', label: 'Formations' },
  { value: 'other', label: 'Autre' }
]

const budgetOptions = [
  { value: 'small', label: 'Moins de 1.000.000 FCFA' },
  { value: 'medium', label: '1.000.000 - 5.000.000 FCFA' },
  { value: 'large', label: '5.000.000 - 10.000.000 FCFA' },
  { value: 'xlarge', label: 'Plus de 10.000.000 FCFA' }
]

const contactForm = ref({
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
})
const quoteForm = ref({
  name: '',
  company: '',
  email: '',
  phone: '',
  service: '',
  description: '',
  budget: ''
})
const uploadedFiles = ref([])
const fileInput = ref(null)

const triggerFileUpload = () => {
  fileInput.value?.click()
}

const submitContactForm = () => {
  // Contact form submission logic would go here
  alert('Merci pour votre message ! Nous vous répondrons dans les plus brefs délais.')
  contactForm.value = {
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  }
}

const submitQuoteForm = () => {
  // Quote form submission logic would go here
  alert('Votre demande de devis a été envoyée ! Notre équipe vous contactera sous 48h.')
  quoteForm.value = {
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    description: '',
    budget: ''
  }
  uploadedFiles.value = []
}

const handleFileUpload = (event) => {
  const files = Array.from(event.target.files)
  uploadedFiles.value = [...uploadedFiles.value, ...files]
}

const removeFile = (index) => {
  uploadedFiles.value.splice(index, 1)
}
</script>
